import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { setupCanvasForHighDPI } from './canvasUtils';

const CanvasContainer = styled.div`
  position: relative;
  overflow: hidden;
  background: #1a1a1a;
  width: 100%;
  height: 100%;
`;

const CanvasPositioner = styled.div.attrs<{ $left: number }>(props => ({
  style: {
    left: `${props.$left}px`
  }
}))`
  position: absolute;
  top: 0;
  bottom: 0;
`;

const Canvas = styled.canvas`
  display: block;
`;

interface SynchronizedWaveformCanvasProps {
  startTime: number;
  width: number;
  height: number;
  audioBuffer: AudioBuffer | null;
  pixelsPerSecond: number;
  visibleStartTime: number;
  visibleEndTime: number;
  color?: string;
  backgroundColor?: string;
  className?: string;
}

export const SynchronizedWaveformCanvas: React.FC<SynchronizedWaveformCanvasProps> = ({
  startTime,
  width,
  height,
  audioBuffer,
  pixelsPerSecond,
  visibleStartTime,
  visibleEndTime,
  color = '#4caf50',
  backgroundColor = '#1a1a1a',
  className
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastRenderRef = useRef<{
    startTime: number;
    endTime: number;
    pixelsPerSecond: number;
    canvasLeft: number;
  } | null>(null);

  // Calculate buffer for smooth scrolling
  const getBufferTime = useCallback((visibleDuration: number) => {
    // Calculate base buffer
    //const baseBuffer = Math.min(30, Math.max(10, visibleDuration * 0.5));
    const baseBuffer =  visibleDuration * 4;
    // Round to 5-second increments to prevent drift
    return Math.round(baseBuffer / 5) * 5;
  }, []);

  const drawWaveform = useCallback(() => {
   
    const canvas = canvasRef.current;
    if (!canvas || !audioBuffer) return;

    // Check if audio track intersects with visible timeline - skip if completely off-screen
    if (audioBuffer.duration + startTime < visibleStartTime  || visibleEndTime < startTime) {
      //console.log('audio track is off screen', audioBuffer.duration + startTime, visibleStartTime, visibleEndTime);
       return;
    }

    const visibleDuration = visibleEndTime - visibleStartTime;
    const bufferTime = getBufferTime(visibleDuration);
    const lastRender = lastRenderRef.current;
    
    // Use stable buffer regions - only recalculate when entering buffer boundaries
    let renderStartTime: number;
    let renderEndTime: number;
    let needsUpdate = false;
    
    if (!lastRender || lastRender.pixelsPerSecond !== pixelsPerSecond) {
      needsUpdate = true;
      console.log('BUFFER UPDATE: Zoom changed or first render');
    } else {


        
        const leftBoundary = lastRender.startTime;
        const rightBoundary = lastRender.endTime;
        
      if((visibleStartTime < leftBoundary && leftBoundary.toFixed(2) !== (startTime).toFixed(2)) || 
      (rightBoundary < visibleEndTime && rightBoundary.toFixed(2) !== (audioBuffer.duration + startTime).toFixed(2))){
        needsUpdate = true;
        console.log('visible region moved outside buffer', visibleStartTime, leftBoundary, rightBoundary,visibleEndTime,(audioBuffer.duration + startTime).toFixed(2));
      }
       
    }
    
    if (needsUpdate) {
      // Calculate new stable render region with buffer
      renderStartTime = Math.round(Math.max(startTime, visibleStartTime - bufferTime) * 100) / 100;
      renderEndTime = Math.round(Math.min(audioBuffer.duration + startTime, visibleEndTime + bufferTime) * 100) / 100;
      
      console.log('BUFFER UPDATE: visible=', visibleStartTime.toFixed(2), '-', visibleEndTime.toFixed(2), 
                  'render=', renderStartTime.toFixed(2), '-', renderEndTime.toFixed(2));
      
    } else {
      // Keep existing stable render region
      renderStartTime = lastRender!.startTime;
      renderEndTime = lastRender!.endTime;
      //console.log('BUFFER STABLE: keeping render region', renderStartTime.toFixed(2), '-', renderEndTime.toFixed(2));
      return; // No redraw needed
    }
    
    const renderDuration = renderEndTime - renderStartTime;
    // Calculate canvas width - limit to prevent browser issues
    const maxCanvasWidth = 32768;
    const idealWidth = Math.round(renderDuration * pixelsPerSecond);
    const canvasWidth = Math.min(maxCanvasWidth, idealWidth);
    
    // Set up canvas
    const ctx = setupCanvasForHighDPI(canvas, canvasWidth, height);
    
    // Clear canvas
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, height);
    
    const renderStartTimeRelative = renderStartTime - startTime;
    const renderEndTimeRelative = renderEndTime - startTime;
    // Draw waveform
    const sampleRate = audioBuffer.sampleRate;
    const channelData = audioBuffer.getChannelData(0);
    
    const startSampleIndex = Math.floor(renderStartTimeRelative * sampleRate);
    const endSampleIndex = Math.min(Math.floor(renderEndTimeRelative * sampleRate), channelData.length);
    
    if (startSampleIndex < channelData.length && endSampleIndex > startSampleIndex) {
      const samplesPerPixel = sampleRate / pixelsPerSecond;
      const centerY = height / 2;
      const amplitude = (height - 4) / 2;
      
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      
      if (pixelsPerSecond < 50) {
        // Low zoom: RMS bars
        for (let x = 0; x < canvasWidth; x++) {
          const timeAtPixel = renderStartTimeRelative + (x / pixelsPerSecond);
          if (timeAtPixel > audioBuffer.duration) break;
          
          const sampleIndex = Math.floor(timeAtPixel * sampleRate);
          const endIndex = Math.min(sampleIndex + samplesPerPixel, channelData.length);
          
          if (sampleIndex >= channelData.length) break;
          
          let sum = 0;
          let count = 0;
          for (let i = sampleIndex; i < endIndex && i < channelData.length; i++) {
            sum += channelData[i] * channelData[i];
            count++;
          }
          
          const rms = count > 0 ? Math.sqrt(sum / count) : 0;
          const waveformHeight = rms * amplitude;
          
          ctx.fillRect(x, centerY - waveformHeight, 1, waveformHeight * 2);
        }
      } else {
        // High zoom: detailed waveform
        ctx.beginPath();
        let hasStarted = false;
        
        for (let x = 0; x < canvasWidth; x++) {
          const timeAtPixel = renderStartTimeRelative + (x / pixelsPerSecond);
          if (timeAtPixel > audioBuffer.duration) break;
          
          const sampleIndex = Math.floor(timeAtPixel * sampleRate);
          if (sampleIndex >= channelData.length) break;
          
          const sample = channelData[sampleIndex];
          const y = centerY - (sample * amplitude);
          
          if (!hasStarted) {
            ctx.moveTo(x, y);
            hasStarted = true;
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.stroke();
      }
    }
    
    // Draw center line
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(canvasWidth, height / 2);
    ctx.stroke();
    
    // Update render info
    lastRenderRef.current = {
      startTime: renderStartTime,
      endTime: renderEndTime,
      pixelsPerSecond,
      canvasLeft: Math.round(renderStartTimeRelative * pixelsPerSecond)
    };
    
  }, [audioBuffer, visibleStartTime, visibleEndTime, pixelsPerSecond, height, color, backgroundColor, getBufferTime]);

  // Draw waveform when needed
  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  // Calculate position - matching ruler's approach
  const canvasLeft = lastRenderRef.current 
    ? Math.round(lastRenderRef.current.canvasLeft)
    : Math.round(visibleStartTime * pixelsPerSecond);

    //console.log('canvasLeft', canvasLeft);
  return (
    <CanvasContainer className={className}>
      <CanvasPositioner $left={canvasLeft}>
        <Canvas ref={canvasRef} />
      </CanvasPositioner>
    </CanvasContainer>
  );
};

export default SynchronizedWaveformCanvas;